import { prepare, layout } from "@chenglou/pretext";

// === Pure widow-fix logic ====================================
// Given a text body and a lines-provider (a closure that returns the
// laid-out lines), decide whether to insert a non-breaking space before
// the orphan word. Returns the modified text or null if no fix.
export function computeWidowFix(text, linesProvider) {
  const lines = linesProvider();
  if (lines.length < 2) return null;

  const lastLine = lines[lines.length - 1];
  if (lastLine.text.length > 16) return null;

  const orphanStart = text.length - lastLine.text.length;
  const cut = text.lastIndexOf(" ", orphanStart - 1);
  if (cut < 0) return null;

  return text.slice(0, cut) + " " + text.slice(cut + 1);
}

// === DOM injection ============================================

function pad2(n) { return String(n).padStart(2, "0"); }

function injectNumerals(rootEl) {
  let i = 1;
  for (const p of rootEl.querySelectorAll(":scope > p")) {
    if (p.querySelector(".prose-num")) continue;
    const span = document.createElement("span");
    span.className = "prose-num";
    span.textContent = pad2(i);
    p.insertBefore(span, p.firstChild);
    p.dataset.proseIdx = String(i);
    i++;
  }
}

function injectSections(rootEl, sections) {
  // `sections` is an array of { roman, title, after } where `after` is the
  // 1-based paragraph index BEFORE which to insert the section header.
  const allP = [...rootEl.querySelectorAll(":scope > p")];
  // Iterate in reverse so earlier insertions don't shift later indices.
  for (let i = sections.length - 1; i >= 0; i--) {
    const sec = sections[i];
    const target = allP[sec.after - 1];
    if (!target) continue;

    const wrap = document.createElement("div");
    wrap.className = "prose-section";
    wrap.dataset.section = sec.roman;
    wrap.innerHTML = `
      <span class="prose-section-rule left"></span>
      <span class="prose-section-label">§ ${sec.roman} · ${sec.title}</span>
      <span class="prose-section-rule right"></span>
    `;
    rootEl.insertBefore(wrap, target);
  }
}

// === Pretext-driven widow fix on real paragraphs ===============

function applyWidowFix(pEl) {
  if (!pEl.dataset.original) {
    const numEl = pEl.querySelector(".prose-num");
    let original = pEl.textContent;
    if (numEl) original = original.replace(numEl.textContent, "");
    pEl.dataset.original = original.trim();
  }
  const original = pEl.dataset.original;

  const cs = getComputedStyle(pEl);
  const fontSize = cs.fontSize;
  const family = cs.fontFamily;
  const weight = cs.fontWeight;
  const font = `${weight} ${fontSize} ${family}`;
  const lineHeightPx = parseFloat(cs.lineHeight);
  const widthPx = pEl.clientWidth;
  if (widthPx < 50) return;

  let prepared;
  try {
    prepared = prepare(original, font);
  } catch {
    return;
  }
  let result;
  try {
    result = layout(prepared, widthPx, lineHeightPx);
  } catch {
    return;
  }
  const lines = result && result.lines ? result.lines : [];
  if (lines.length < 2) {
    writeText(pEl, original);
    return;
  }
  const fixed = computeWidowFix(original, () => lines);
  writeText(pEl, fixed !== null ? fixed : original);
}

function writeText(pEl, text) {
  const numEl = pEl.querySelector(".prose-num");
  pEl.textContent = "";
  if (numEl) pEl.appendChild(numEl);
  pEl.appendChild(document.createTextNode(text));
}

// === Public API ===============================================

export function mountProse(rootEl, { numbered = false, sections = null } = {}) {
  if (numbered) injectNumerals(rootEl);
  if (sections) injectSections(rootEl, sections);

  const paragraphs = [...rootEl.querySelectorAll(":scope > p")];
  function fixAll() {
    for (const p of paragraphs) applyWidowFix(p);
  }
  fixAll();

  // Re-run widow fix on resize, debounced.
  let resizeT;
  window.addEventListener("resize", () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(fixAll, 200);
  });

  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return;

  // Numeral fade as paragraph approaches viewport center
  const numObs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) e.target.classList.toggle("is-active", e.isIntersecting);
    },
    { rootMargin: "-25% 0% -50% 0%" },
  );
  for (const p of paragraphs) numObs.observe(p);

  // Section rule draw-in on first entry
  const sectionEls = [...rootEl.querySelectorAll(".prose-section")];
  const secObs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("is-active");
          secObs.unobserve(e.target);
        }
      }
    },
    { rootMargin: "0% 0% -10% 0%" },
  );
  for (const s of sectionEls) secObs.observe(s);

  // Figure parallax
  const figures = [...rootEl.querySelectorAll("figure")];
  function updateParallax() {
    const vh = window.innerHeight;
    for (const fig of figures) {
      const r = fig.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) continue;
      const center = (r.top + r.bottom) / 2;
      const t = (center - vh / 2) / (vh / 2);
      const px = Math.max(-12, Math.min(12, -t * 12));
      const img = fig.querySelector("img");
      if (img) img.style.setProperty("--parallax", `${px}px`);
    }
  }
  let parallaxRaf;
  function scheduleParallax() {
    if (parallaxRaf) return;
    parallaxRaf = requestAnimationFrame(() => { updateParallax(); parallaxRaf = 0; });
  }
  window.addEventListener("scroll", scheduleParallax, { passive: true });
  window.addEventListener("resize", scheduleParallax);
  scheduleParallax();
}
